import React from 'react';

import {UserSuggestList} from 'src/components/user-suggest-list/user-suggest-list';
import {Role} from 'src/openapi/admin/models/Role';
import {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';
import {Vendor} from 'src/openapi/admin/models/Vendor';
import {isVendorAdminRole} from 'src/utils/role-utils';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Input} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';


type Props = {
    emailInput: string,
    shown: boolean,
    roles: Role[],
    vendors: Vendor[],
    roleId?: string,
    vendorId?: string,
    disableRole: boolean,
    firstName: string,
    lastName: string,
    suggestUsers: UserShortInfo[],
    searchLoading: boolean,
    hasSearched: boolean,
    isMinQueryShown: boolean,
    onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    onUserSelect: (user: UserShortInfo) => void,
    onClearSuggest: () => void,
    onSetShown: (shown: boolean) => void,
    onHandleRoleChange: (roleId: string | number | null) => void,
    onHandleVendorChange: (vendorId: string | number | null) => void,
    onBlur?: () => void,
};

const MAX_LEN = 100;

export const AddUserForm: React.FC<Props> = ({
    emailInput,
    shown,
    roles,
    vendors,
    roleId,
    vendorId,
    disableRole,
    firstName,
    lastName,
    suggestUsers,
    searchLoading,
    hasSearched,
    isMinQueryShown,
    onEmailChange,
    onUserSelect,
    onClearSuggest,
    onSetShown,
    onHandleRoleChange,
    onHandleVendorChange,
    onBlur,
}) => {
    const roleItems: Array<{value: string, label: string}> = roles.map(role => ({
        value: role.id,
        label: role.name,
    })).sort((a, b) => a.label.localeCompare(b.label));

    const vendorItems: Array<{value: string, label: string}> = vendors.map(vendor => ({
        value: vendor.id,
        label: vendor.vendorName,
    }));

    const shouldShowSuggest = shown
        && emailInput.trim().length > 0
        && (searchLoading || suggestUsers.length > 0 || isMinQueryShown);

    return (
        <Group gap="m" direction="v">
            <Item>
                <Label htmlFor="addUserEmailInputId">Email</Label>
                <Autosuggest
                    id="addUserEmailInputId"
                    value={emailInput}
                    shown={shouldShowSuggest}
                    setShown={onSetShown}
                    onChange={onEmailChange}
                    placeholder="Enter user email"
                    maxLength={MAX_LEN}
                    onItemSelect={onUserSelect}
                    onClear={onClearSuggest}
                    onBlur={onBlur}>
                    <UserSuggestList
                        id="user-suggest-list"
                        busy={searchLoading}
                        users={suggestUsers}
                        hasSearched={hasSearched}
                        isMinQueryShown={isMinQueryShown}
                    />
                </Autosuggest>
            </Item>
            <Item>
                <Label htmlFor="addUserFirstNameInputId">First Name</Label>
                <Input
                    id="addUserFirstNameInputId"
                    readOnly={true}
                    value={firstName}
                    placeholder="First name"
                    endAdornment={<Icon type={Icons.LOCK} size="Size_14"/>}
                />
            </Item>
            <Item>
                <Label htmlFor="addUserLastNameInputId">Last Name</Label>
                <Input
                    id="addUserLastNameInputId"
                    readOnly={true}
                    value={lastName}
                    placeholder="Last name"
                    endAdornment={<Icon type={Icons.LOCK} size="Size_14"/>}
                />
            </Item>
            <Item>
                <Label htmlFor="addUserRoleSelectId">Role</Label>
                <ComboBox<string | undefined, string>
                    id="addUserRoleSelectId"
                    value={roleId}
                    onChange={onHandleRoleChange}
                    placeholder="Select role"
                    disabled={disableRole}>
                    {roleItems.map(role => (
                        <ComboboxItem key={role.value} caption={role.label} value={role.value}/>
                    ))}
                </ComboBox>
            </Item>
            {isVendorAdminRole(roleId) ? (
                <Item>
                    <Label htmlFor="addUserVendorSelectId">Vendor Name</Label>
                    <ComboBox<string | undefined, string>
                        id="addUserVendorSelectId"
                        value={vendorId}
                        onChange={onHandleVendorChange}
                        placeholder="Select vendor"
                        disabled={disableRole}>
                        {vendorItems.map(vendor => (
                            <ComboboxItem key={vendor.value} caption={vendor.label} value={vendor.value}/>
                        ))}
                    </ComboBox>
                </Item>
            ) : null}
        </Group>
    );
};
